import axios, { AxiosResponse } from 'axios'

import { ResponseSuccess } from '@models/apiBase'
import { RequestQuery } from '@models/apiBase'
import { Response } from '@models/apiBase'
import { WithFilter } from '@models/apiBase'
import { RelationTypeEnum } from '@models/base'
import { GenderEnum } from '@models/base'
import { ID } from '@models/base'
import { PatientRelativeModel } from '@models/patientRelative'

import { API_URL } from '@/constants/constant'

const PATIENT_RELATIVES_URL = `${API_URL}/patient-relatives`

type ResponsePatientRelative = Response<{
  patient_relative: PatientRelativeModel
}>
type ResponsePatientRelatives = Response<WithFilter<Array<PatientRelativeModel>>>

type QueryFilterByPatient = RequestQuery<{
  patient_id: ID
}>

export type PayloadCreatePatientRelative = {
  birth_date: string | null
  business_phone_number: string | null
  family_card_number: string | null
  first_name: string | null
  gender: GenderEnum | null
  home_phone_number: string | null
  last_name: string | null
  living_address_1: string | null
  living_address_2: string | null
  living_rt: string | null
  living_rw: string | null
  living_country: string | null
  living_first_level_administrative_division_id: ID
  living_fourth_level_administrative_division_id: ID
  living_postal_code: string | null
  living_second_level_administrative_division_id: ID
  living_third_level_administrative_division_id: ID
  mobile_phone_number: string | null
  nric_address_1: string | null
  nric_address_2: string | null
  nric_rt: string | null
  nric_rw: string | null
  nric_country: string | null
  nric_first_level_administrative_division_id: ID
  nric_fourth_level_administrative_division_id: ID
  nric_number: string | null
  nric_postal_code: string | null
  nric_second_level_administrative_division_id: ID
  nric_third_level_administrative_division_id: ID
  passport_number: string | null
  patient_id: ID
  related_patient_id: ID
  relation_type: RelationTypeEnum
}
export type PayloadUpdatePatientRelative = {
  birth_date: string | null
  business_phone_number: string | null
  family_card_number: string | null
  first_name: string | null
  gender: GenderEnum
  home_phone_number: string | null
  last_name: string | null
  living_address_1: string | null
  living_address_2: string | null
  living_rt: string | null
  living_rw: string | null
  living_country: string | null
  living_first_level_administrative_division_id: ID
  living_fourth_level_administrative_division_id: ID
  living_postal_code: string | null
  living_second_level_administrative_division_id: ID
  living_third_level_administrative_division_id: ID
  mobile_phone_number: string | null
  nric_address_1: string | null
  nric_address_2: string | null
  nric_rt: string | null
  nric_rw: string | null
  nric_country: string | null
  nric_first_level_administrative_division_id: ID
  nric_fourth_level_administrative_division_id: ID
  nric_number: string | null
  nric_postal_code: string | null
  nric_second_level_administrative_division_id: ID
  nric_third_level_administrative_division_id: ID
  passport_number: string | null
  patient_id: ID
  related_patient_id: ID
  relation_type: RelationTypeEnum
}

export type PayloadFilterPatientRelatives = RequestQuery<{
  patient_id: ID
}>

interface IPatientRelativesAPI {
  filter: (query: PayloadFilterPatientRelatives) => Promise<ResponsePatientRelatives>

  create: (payload: PayloadCreatePatientRelative) => Promise<ResponsePatientRelative>

  update: (id: ID, payload: PayloadUpdatePatientRelative) => Promise<ResponsePatientRelative>

  delete: (id: ID) => Promise<ResponseSuccess>

  setDefault: (id: ID) => Promise<ResponsePatientRelative>

  getOptionsForPatientVisitForm: (query: QueryFilterByPatient) => Promise<ResponsePatientRelatives>

  getOptionsForQueueNumberForm: (query: QueryFilterByPatient) => Promise<ResponsePatientRelatives>
}

export const PatientRelativesAPI: IPatientRelativesAPI = {
  filter: async (query) => {
    return axios
      .post(`${PATIENT_RELATIVES_URL}/filter`, query)
      .then((r: AxiosResponse<ResponsePatientRelatives>) => r.data)
  },

  create: async (payload) => {
    return axios.post(PATIENT_RELATIVES_URL, payload).then((r: AxiosResponse<ResponsePatientRelative>) => r.data)
  },

  update: async (id, payload) => {
    return axios
      .put(`${PATIENT_RELATIVES_URL}/${id}`, payload)
      .then((r: AxiosResponse<ResponsePatientRelative>) => r.data)
  },

  delete: async (id) => {
    return axios.delete(`${PATIENT_RELATIVES_URL}/${id}`).then((r: AxiosResponse<ResponseSuccess>) => {
      return r.data
    })
  },

  setDefault: async (id) => {
    return axios
      .patch(`${PATIENT_RELATIVES_URL}/${id}/set-default`)
      .then((r: AxiosResponse<ResponsePatientRelative>) => r.data)
  },

  getOptionsForPatientVisitForm: async (query) => {
    return axios
      .post(`${PATIENT_RELATIVES_URL}/options/patient-visit-form`, query)
      .then((r: AxiosResponse<ResponsePatientRelatives>) => r.data)
  },

  getOptionsForQueueNumberForm: async (query) => {
    return axios
      .post(`${PATIENT_RELATIVES_URL}/options/queue-number-form`, query)
      .then((r: AxiosResponse<ResponsePatientRelatives>) => r.data)
  },
}
